import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ProjectCard, ProjectCardSkeleton } from '../components/ProjectCard';
import { TrustScoreDisplay } from '../components/TrustBadge';
import { FollowButton } from '../components/FollowButton';
import { FollowCounts } from '../components/FollowCounts';
import { UpdateCard } from '../components/UpdateCard';
import { UpdateComposer } from '../components/UpdateComposer';
import {
  Calendar,
  Zap,
  Rocket,
  Heart,
  Award,
  MessageCircle,
  Settings,
  FileText,
} from 'lucide-react';
import { userAPI, updateAPI, messageAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { formatRelativeTime, getAvatarFallback, formatNumber } from '../lib/utils';
import { toast } from 'sonner';

export function ProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isOwnProfile = currentUser?.user_id === userId;

  const fetchProfile = useCallback(async () => {
    try {
      const response = await userAPI.getProfile(userId);
      setProfile(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const fetchUpdates = useCallback(async () => {
    try {
      const res = await updateAPI.getUserUpdates(userId);
      setUpdates(res.data.updates || []);
    } catch (err) {
      // ignore
    }
  }, [userId]);

  useEffect(() => {
    fetchProfile();
    fetchUpdates();
  }, [fetchProfile, fetchUpdates]);

  const handleSendMessage = async () => {
    try {
      const res = await messageAPI.createConversation(userId);
      navigate(`/messages/${res.data.conversation_id}`);
    } catch (err) {
      toast.error('Failed to start conversation');
    }
  };

  const handleUpdateCreated = (newUpdate) => {
    setUpdates(prev => [newUpdate, ...prev]);
  };

  const handleDeleteUpdate = async (updateId) => {
    try {
      await updateAPI.delete(updateId);
      setUpdates(prev => prev.filter(u => u.update_id !== updateId));
      toast.success(t('component.updateDeleted'));
    } catch (err) {
      toast.error('Failed to delete update');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('profile.userNotFound')}</h2>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Profile Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            <Avatar className="w-24 h-24 border-4 border-white shadow-lg">
              <AvatarImage src={profile.picture} alt={profile.name} />
              <AvatarFallback className="text-2xl bg-orange-100 text-orange-700">
                {getAvatarFallback(profile.name)}
              </AvatarFallback>
            </Avatar>

            <div className="text-center sm:text-left flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-slate-900">
                  {profile.name}
                </h1>
                <div className="flex items-center gap-2 justify-center sm:justify-start">
                  {!isOwnProfile && isAuthenticated && (
                    <>
                      <FollowButton userId={userId} onFollowChange={fetchProfile} />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSendMessage}
                        className="text-slate-600"
                      >
                        <MessageCircle className="w-4 h-4 mr-1" />
                        {t('profile.message')}
                      </Button>
                    </>
                  )}
                  {isOwnProfile && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/settings/profile')}
                    >
                      <Settings className="w-4 h-4 mr-1" />
                      {t('profile.editProfile')}
                    </Button>
                  )}
                </div>
              </div>

              {profile.bio && (
                <p className="text-slate-600 mb-3">{profile.bio}</p>
              )}

              <div className="mb-3">
                <FollowCounts
                  userId={userId}
                  followersCount={profile.followers_count || 0}
                  followingCount={profile.following_count || 0}
                />
              </div>

              <div className="flex flex-wrap justify-center sm:justify-start gap-4">
                <div className="flex items-center gap-1 text-sm text-slate-600">
                  <Calendar className="w-4 h-4" />
                  {t('profile.joined', { time: formatRelativeTime(profile.created_at) })}
                </div>
                <div className="flex items-center gap-1 text-sm text-slate-600">
                  <Zap className="w-4 h-4 text-orange-500" />
                  {t('profile.boostsGiven', { count: formatNumber(profile.total_boosts_given) })}
                </div>
              </div>

              {profile.badges?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {profile.badges.map((badge) => (
                    <Badge key={badge} variant="secondary">
                      <Award className="w-3 h-3 mr-1" />
                      {badge}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 space-y-6">
            <TrustScoreDisplay
              level={profile.trust_level}
              score={profile.trust_score}
            />
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="font-bold text-slate-900 mb-4">{t('profile.stats')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <StatCard icon={Rocket} value={profile.projects?.length || 0} label={t('profile.projects')} />
                <StatCard icon={Heart} value={profile.recent_boosts?.length || 0} label={t('profile.recentBoosts')} />
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <Tabs defaultValue="projects">
              <TabsList className="mb-6">
                <TabsTrigger value="projects">{t('profile.projects')}</TabsTrigger>
                <TabsTrigger value="updates">
                  <FileText className="w-4 h-4 mr-1" />
                  {t('profile.updates')}
                </TabsTrigger>
                <TabsTrigger value="boosts">{t('profile.recentBoosts')}</TabsTrigger>
              </TabsList>

              <TabsContent value="projects">
                {profile.projects?.length > 0 ? (
                  <div className="grid sm:grid-cols-2 gap-6">
                    {profile.projects.map((project) => (
                      <ProjectCard key={project.project_id} project={project} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
                    <Rocket className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">{t('profile.noProjects')}</h3>
                    <p className="text-slate-500">{t('profile.noProjectsDesc')}</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="updates">
                {isOwnProfile && (
                  <UpdateComposer userProjects={profile.projects || []} onUpdateCreated={handleUpdateCreated} />
                )}
                {updates.length > 0 ? (
                  <div className="space-y-4">
                    {updates.map((update) => (
                      <UpdateCard key={update.update_id} update={update} showUser={false} onDelete={isOwnProfile ? handleDeleteUpdate : undefined} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
                    <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">{t('profile.noUpdates')}</h3>
                    <p className="text-slate-500">{isOwnProfile ? t('profile.shareFirst') : t('profile.noUpdatesDesc')}</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="boosts">
                {profile.recent_boosts?.length > 0 ? (
                  <div className="space-y-3">
                    {profile.recent_boosts.map((boost) => (
                      <BoostActivityCard key={boost.boost_id} boost={boost} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
                    <Zap className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">{t('profile.noBoosts')}</h3>
                    <p className="text-slate-500">{t('profile.noBoostsDesc')}</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, value, label }) {
  return (
    <div className="text-center p-4 bg-slate-50 rounded-xl">
      <Icon className="w-5 h-5 mx-auto text-slate-400 mb-2" />
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}

function BoostActivityCard({ boost }) {
  return (
    <Link
      to={`/project/${boost.project_id}`}
      className="block bg-white rounded-xl border border-slate-200 p-4 hover:border-orange-300 transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
          {boost.boost_type === 'money' ? (
            <span className="text-orange-600 font-bold">${boost.amount}</span>
          ) : (
            <Zap className="w-5 h-5 text-orange-500" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-900 truncate">
            {boost.boost_type === 'money'
              ? `Supported with $${boost.amount}`
              : `${boost.boost_type.charAt(0).toUpperCase() + boost.boost_type.slice(1)}d a project`}
          </p>
          <p className="text-sm text-slate-500">{formatRelativeTime(boost.created_at)}</p>
        </div>
        <Badge variant="secondary" className="capitalize">
          +{boost.score?.toFixed(1)} pts
        </Badge>
      </div>
    </Link>
  );
}
